/**
 * Hardware catalog preset for Tillio POS.
 * Categories: card readers, printers, cash drawers, stands, devices.
 * Items support image, purchaseUrl (empty hides Buy button), and capability chips.
 */

export const hardwareCategories = [
  {
    id: 'card-readers',
    title: 'Card readers',
    intro:
      'Pair Stripe Terminal readers with Tillio POS for chip, swipe, and contactless payments on the Stripe account you connect. Choose a countertop smart reader or a portable Bluetooth model for tableside service.',
    items: [
      {
        id: 'stripe-m2',
        title: 'Stripe Reader M2',
        description:
          'Compact Bluetooth mobile reader for tableside payments, food trucks, and flexible floor service in the US.',
        image: 'https://images.stripeassets.com/fzn2n1nzq965/mnfl223akEEJrvfGQriXT/14092881b660553efa84a3e40bf661f8/Property_1_M2.png',
        purchaseUrl: 'https://stripe.com/terminal/m2',
        capabilities: ['Bluetooth', 'Chip', 'Swipe', 'Tap'],
      },
      {
        id: 'verifone-p630',
        title: 'Verifone P630',
        description:
          'Designed for performance and security, the P630 has a 3.5-in. touchscreen display, downward-facing barcode scanner and tactile, backlit PIN pad',
        image: 'https://images.stripeassets.com/fzn2n1nzq965/7Ay6LQ8Iluqbf77sU5Xsqo/5bd916d28eb3d138980b9e78b7ea463d/Property_1_P630.png',
        purchaseUrl: 'https://stripe.com/terminal/p630',
        capabilities: ['WiFi', 'Ethernet', '4G', 'Bluetooth'],
      },
    ],
  },
  {
    id: 'printers',
    title: 'Printers',
    intro:
      'Most network printers are supported by Tillio POS. We recommend using ethernet printers for best performance.',
    items: [
      {
        id: 'volcora-thermal-printer',
        title: 'Volcora 80mm Thermal Printer',
        description:
          'Compact 80mm thermal receipt printer with multiple interfaces and CloudPRNT options for counter and kitchen tickets.',
        image: 'https://m.media-amazon.com/images/I/61UImEb7-ML._AC_SL1500_.jpg',
        purchaseUrl: 'https://www.amazon.com/dp/B0CPDB7P36',
        capabilities: ['USB', 'WiFi', 'RJ11/12 Cash drawer', '80mm paper'],
      },
      {
        id: 'rongta-thermal-printer',
        title: 'Rongta 80mm Thermal Printer',
        description:
          'Workhorse receipt printer available in USB, LAN, and Bluetooth variants—fast, reliable, and easy to place on a counter.',
        image: 'https://m.media-amazon.com/images/I/61esCL-zfcL._AC_SL1500_.jpg',
        purchaseUrl: 'https://www.amazon.com/dp/B08V4H7T47',
        capabilities: ['Ethernet', 'USB', 'RJ11/12 Cash drawer', '80mm'],
      },
      {
        id: 'mini-thermal-printer',
        title: 'Mini Thermal Receipt Printer',
        description:
          'Light weight and small with built-in rechargeable battery. Equipped with 2000mAh rechargeable battery. Supports Bluetooth(IOS and Android) and USB(Android Only). This printer has a lot of variations and designs and typically brand doesnt matter as long as it supports bluetooth itll most likely work',
        image: 'https://m.media-amazon.com/images/I/61nilZHzBnL._AC_SL1500_.jpg',
        purchaseUrl: 'https://www.amazon.com/dp/B0CL481GS1',
        capabilities: ['Bluetooth', 'USB', 'Generic', 'Rechargeable Battery', '58mm', 'Cheap'],
      },
      {
        id: 'netum-bluetooth-receipt-printer',
        title: 'NETUM 58mm Bluetooth Receipt Printer',
        description:
          'Light weight and small with built-in rechargeable battery. Equipped with 2000mAh rechargeable battery. Supports Bluetooth(IOS and Android) and USB(Android Only)',
        image: 'https://m.media-amazon.com/images/I/711+UCWXnLL._AC_SL1500_.jpg',
        purchaseUrl: 'https://www.amazon.com/dp/B0919HGLSH',
        capabilities: ['Bluetooth', 'USB', 'Rechargeable Battery', '58mm', 'Cheap'],
      },
    ],
  },
  {
    id: 'cash-drawers',
    title: 'Cash drawers',
    intro:
      'Printer-driven cash drawers for cash tips, change, and end-of-night counts. Pair with a receipt printer kick-out cable for automatic open on cash sales.',
    items: [
      {
        id: 'volcora-13-cash-drawer',
        title: 'Volcora 13',
        description:
          'Heavy-duty printer-driven cash drawer for high-volume retail and restaurant use.',
        image: 'https://m.media-amazon.com/images/I/81c6dndHQ7L._AC_SL1500_.jpg',
        purchaseUrl: 'https://www.amazon.com/dp/B0857KM7TN',
        capabilities: ['Printer-driven', '4Bill 5Coin'],
      },
      {
        id: 'volcora-16-cash-drawer',
        title: 'Volcora 16',
        description:
          'Heavy-duty printer-driven cash drawer for high-volume retail and restaurant use.',
        image: 'https://m.media-amazon.com/images/I/81M6nSJ4ixL._AC_SL1500_.jpg',
        purchaseUrl: 'https://www.amazon.com/dp/B0857KM7TN',
        capabilities: ['Printer-driven', '4Bill 5Coin'],
      },
    ],
  },
  {
    id: 'stands',
    title: 'Stands',
    intro:
      'Secure tablet stands and enclosures for a clean command station—cable management, swivel for guest-facing checkout, and space for a printer underneath when you need it.',
    items: [
      {
        id: 'heckler-windfall',
        title: 'Bouncepad - Dual Tablet Holder Mount',
        description:
          'designed for desk and counter-based POS environments where both staff and customers interact with the screen. It combines the Clip jaw system for the main tablet with a Core mounting solution for the secondary display',
        image: 'https://us.bouncepad.com/cdn/shop/files/Twin_Clip_White_85148530-83ec-48c5-8420-3fcb6934e0e3_1500x.jpg',
        purchaseUrl: 'https://us.bouncepad.com/products/dual_tablet_mount_pos',
        capabilities: ['8-13 Tablets', 'Secure enclosure', 'USB C', 'Rotating Base'],
      },
      {
        id: 'bouncepad-core-flip',
        title: 'Bouncepad - Core Flip',
        description:
          'secure, freestanding tablet and iPad stand with an integrated flipping mechanism that makes your tablet screen easy to share between users',
        image: 'https://us.bouncepad.com/cdn/shop/files/Bouncepad_Core_-_Flip_-_White_-_Tablet_and_iPad_Kiosk_Stand_be2ac271-e91b-4b20-b65c-87c3e76363f7_1500x.jpg',
        purchaseUrl: 'https://us.bouncepad.com/products/ipad-kiosk-stand-core-flip',
        capabilities: ['Rotating Base', 'USB C'],
      },
    ],
  },
  {
    id: 'devices',
    title: 'Devices',
    intro:
      'Works with most tablets on iOS and Android. We recommend iPad 8 and above.',
    items: [],
  },
]

export function getHardwareCategory(id) {
  return hardwareCategories.find((c) => c.id === id)
}

export function getHardwareItems(categoryId = 'all') {
  if (categoryId === 'all') {
    return hardwareCategories.flatMap((c) =>
      c.items.map((item) => ({ ...item, categoryId: c.id }))
    )
  }
  const category = getHardwareCategory(categoryId)
  return category ? category.items.map((item) => ({ ...item, categoryId })) : []
}
